import { SYNC_STATUS } from '@yandex-int/k-common/typings';
import cx from 'classnames';
import qs from 'query-string';
import { Select, SelectEvent, useGeneratePath, useStaticUrls } from 'platform-components';
import { POPUP_AXIS, SELECT_THEMES } from 'platform-components/constants';
import { getExactPathMatch } from 'platform-components/utils';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { matchPath } from 'react-router-dom';
import ym from 'react-yandex-metrika';

import { isMesTeam } from 'common.components/classes/utils';
import { teamBuilderActions } from 'common.components/team-builder/actions';
import { TEAM_BUILDER_ENTRY_STEPS } from 'common.components/team-builder/typings';
import { Button } from '@edu-frontend/modules/src/components/ui/Button';
import { IconPlus } from '@edu-frontend/modules/src/components/ui/Icon';
import * as userActions from 'common.components/user/actions';
import { getCurrentClass, getClasses } from 'common.components/user/selectors';
import { getInitialSubject } from 'store/subject-config/selectors';
import { getIsInStudentStatsBundle } from 'utils/get-is-in-student-stats-bundle';
import { getTeamIdFromPath } from 'utils/path';

import { ClassesSelectSettings } from './classes-select-settings/classes-select-settings';
import { ClassesSelectTeam } from './classes-select-team/classes-select-team';

import './classes-select.scss';

interface Props {
    mix?: string;
}

export const ClassesSelect = ({ mix }: Props) => {
    const dispatch = useDispatch();
    const generatePath = useGeneratePath();
    const history = useHistory();
    const location = useLocation();
    const staticUrls = useStaticUrls();
    const getRouterPath = useGeneratePath();
    const intl = useIntl();

    const initialSubject = useSelector(getInitialSubject);
    const teams = useSelector(getClasses);
    const currentTeam = useSelector((store) => getCurrentClass(store, { classId: getTeamIdFromPath() }));

    const redirectToClasses = useCallback(() => {
        history.push(`${generatePath(staticUrls.labClasses)}${location.search}`);
    }, [generatePath, history, location.search, staticUrls.labClasses]);

    const redirectToClassStudents = useCallback(
        (teamId: number) => {
            dispatch(userActions.setWizardVariables({ headerMyClassPressed: true }));

            const classStudentsPath = generatePath(staticUrls.labClassStudents, { classId: String(teamId) });

            history.push(`${classStudentsPath}${location.search}`);
        },
        [dispatch, generatePath, history, location.search, staticUrls.labClassStudents]
    );

    const handleSelectSettings = useCallback(() => {
        if (currentTeam) {
            redirectToClassStudents(currentTeam.id);
        } else {
            redirectToClasses();
        }
    }, [currentTeam, redirectToClasses, redirectToClassStudents]);

    const handleSelectTeam = useCallback(
        (teamId: number) => {
            if (teamId === currentTeam?.id) {
                return;
            }

            let replacedClass = location.pathname
                // Сбрасываем выбранный урок, если переходим с витрины КТП
                // https://st.yandex-team.ru/EDUCATION-19984
                .replace(/(tab\/timeline\/)lesson\/\d+\/?/, '$1')
                .replace(/(classes\/)(\d+)/, `$1${teamId}`);

            // Удаляем параметр grade из query, т.к. при смене класса грейд должен быть текущим грейдом класса
            const { grade: _, ...searchParams } = qs.parse(location.search);
            const searchStr = `?${qs.stringify(searchParams)}`;

            const match = getExactPathMatch(staticUrls, replacedClass);

            if (getIsInStudentStatsBundle(replacedClass)) {
                const subject = match?.params.subject || initialSubject;

                // Бандлы разные, переход через history.push невозможен
                window.location.href = `${generatePath(staticUrls.labSubjectLessonsComplete, {
                    ...match?.params,
                    subject,
                })}${searchStr}`;

                return;
            }

            // для страницы спецкурсов сбрасываем курс из урла, чтобы загрузить курсы заново и найти нужный courseId
            if (matchPath(location.pathname, { path: staticUrls.labClassSpecCourse, exact: false })) {
                replacedClass = generatePath(staticUrls.labClassSpecCourseStub, { classId: teamId });
            }

            ym('reachGoal', 'teacher_class_change');

            history.push(`${replacedClass}${searchStr}`);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [generatePath, history, initialSubject, location.search, location.pathname, staticUrls]
    );

    const handleSelect = useCallback(
        (option: SelectEvent<number>) => {
            const teamId = Number(option.target.value);
            const isSettingsOptionSelected = teamId === 0;

            if (isSettingsOptionSelected) {
                handleSelectSettings();
            } else {
                handleSelectTeam(teamId);
            }
        },
        [handleSelectTeam, handleSelectSettings]
    );

    const handleFocus = useCallback(() => {
        ym('reachGoal', 'teacher_my_students_entered');
    }, []);

    const selectOptions = useMemo(() => {
        if (teams.length === 0) {
            return [];
        }

        return teams
            .map((team) => (
                <Select.Option disabled={team.sync_status === SYNC_STATUS.FIRST_SYNC} key={team.id} value={team.id}>
                    <ClassesSelectTeam grade={team.level} isMes={isMesTeam(team)} name={team.name} />
                </Select.Option>
            ))
            .concat(<div className="classes-select__delimiter" key="delimiter" />)
            .concat(
                <Select.Option key="settings" value={0}>
                    <ClassesSelectSettings />
                </Select.Option>
            );
    }, [teams]);

    // открываем флоу модалок с добавлением школы, класса и учеников
    const handleAddStudentsClick = useCallback(() => {
        dispatch(
            teamBuilderActions.start({
                entryPoint: TEAM_BUILDER_ENTRY_STEPS.TEAM,
                onSuccess: (addedStudentsIds, createdTeamId) => {
                    let redirectPath = staticUrls.labClasses;
                    const { path = '' } = getExactPathMatch(staticUrls, location.pathname) || {};
                    const newPath = path.replace('/lab/', '/lab/classes/:classId/');

                    if (Object.values(staticUrls).includes(newPath)) {
                        redirectPath = newPath;
                    }

                    history.push(getRouterPath(redirectPath, { classId: createdTeamId }));
                },
            })
        );
    }, [dispatch, getRouterPath, history, location.pathname, staticUrls]);

    if (selectOptions.length === 0) {
        return (
            <Button className={cx(mix)} onClick={handleAddStudentsClick} size="m" view="brand">
                <div className="classes-select__add-students-button">
                    <IconPlus size="s" />
                    {intl.formatMessage({ id: 'common.header.addStudents' })}
                </div>
            </Button>
        );
    }

    return (
        <Select
            mix={cx('classes-select', mix)}
            onFocus={handleFocus}
            onSelect={handleSelect}
            popupAxis={POPUP_AXIS.END}
            popupMaxWidth="240"
            popupMinWidth="167"
            theme={SELECT_THEMES.ROUNDED}
            value={currentTeam?.id || 0}
            width="auto"
        >
            {selectOptions}
        </Select>
    );
};
